/**
 * Lightweight analytics tracker — sends page views and events
 * to the Intrlock events API (DynamoDB-backed).
 *
 * Tracks: page views, session duration, referrer, user agent, screen size
 * across all domains: intrlock.io, app.intrlock.io, visitor.intrlock.io
 */
import { apiOrigin } from './surface';

const API = apiOrigin();
const SESSION_KEY = 'intrlock_analytics_session';

interface AnalyticsEvent {
  event_type: string;
  page_url?: string;
  page_path?: string;
  referrer?: string;
  domain?: string;
  screen_width?: number;
  screen_height?: number;
  user_agent?: string;
  session_id?: string;
  user_id?: string;
  metadata?: Record<string, any>;
}

function getSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function getUserId(): string | undefined {
  try {
    const user = localStorage.getItem('intrlock_user');
    if (user) {
      const parsed = JSON.parse(user);
      return parsed.uuid || parsed.id || parsed.email;
    }
  } catch {}
  return undefined;
}

export function trackEvent(event: AnalyticsEvent): void {
  const ua = event.user_agent || navigator.userAgent;
  const device = parseDeviceFromUA(ua);
  const payload = {
    event_type: event.event_type,
    page_url: event.page_url || window.location.href,
    page_path: event.page_path || window.location.pathname,
    referrer: event.referrer || document.referrer || null,
    domain: event.domain || window.location.hostname,
    screen_width: event.screen_width || window.innerWidth,
    screen_height: event.screen_height || window.innerHeight,
    user_agent: ua,
    session_id: event.session_id || getSessionId(),
    user_id: event.user_id || getUserId(),
    metadata: { ...device, ...(event.metadata || {}) },
    property_id: null,
  };

  // Fire and forget — don't block the UI
  fetch(`${API}/events/visitor`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

/**
 * Visitor-side page-view tracker. Stamps property_id + intercom_config_id so
 * the property/intercom analytics bucket each view correctly. Also returns a
 * cleanup function the caller invokes on unmount/navigation to send the
 * page_duration follow-up.
 */
export function trackVisitorView(opts: {
  property_id?: string | null;
  intercom_config_id?: string | null;
  metadata?: Record<string, any>;
}): () => void {
  const enteredAt = Date.now();
  const sessionId = getSessionId();
  const ua = navigator.userAgent;
  const device = parseDeviceFromUA(ua);
  const base = {
    page_url: window.location.href,
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    domain: window.location.hostname,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    user_agent: ua,
    session_id: sessionId,
    user_id: null,
  };
  const propertyId = opts.property_id || null;
  const intercomId = opts.intercom_config_id || null;

  // Initial page view
  fetch(`${API}/events/visitor`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...base,
      event_type: 'page_view',
      property_id: propertyId,
      intercom_config_id: intercomId,
      metadata: { ...device, ...(opts.metadata || {}) },
    }),
  }).catch(() => {});

  // Cleanup → fire page_duration with the elapsed time
  return () => {
    const seconds = Math.round((Date.now() - enteredAt) / 1000);
    if (seconds <= 0 || seconds > 3600) return;
    fetch(`${API}/events/visitor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...base,
        event_type: 'page_duration',
        property_id: propertyId,
        intercom_config_id: intercomId,
        duration_seconds: seconds,
        metadata: { ...device, ...(opts.metadata || {}) },
      }),
      // Use keepalive so the request still flies during page unload
      keepalive: true,
    }).catch(() => {});
  };
}

/** Best-effort device classification from a user-agent string. */
function parseDeviceFromUA(ua: string): {
  device_type: string;
  device_os: string;
  browser: string;
} {
  const lower = ua.toLowerCase();
  let device_type = 'desktop';
  if (/ipad/.test(lower)) device_type = 'tablet';
  else if (/iphone/.test(lower)) device_type = 'mobile';
  else if (/android/.test(lower)) device_type = /mobile/.test(lower) ? 'mobile' : 'tablet';

  let device_os = 'unknown';
  if (/iphone|ipad|ipod/.test(lower)) device_os = 'iOS';
  else if (/android/.test(lower)) device_os = 'Android';
  else if (/windows/.test(lower)) device_os = 'Windows';
  else if (/mac os|macos/.test(lower)) device_os = 'macOS';
  else if (/linux/.test(lower)) device_os = 'Linux';

  let browser = 'unknown';
  if (/edg\//.test(lower)) browser = 'Edge';
  else if (/chrome\//.test(lower) && !/edg\//.test(lower)) browser = 'Chrome';
  else if (/safari\//.test(lower) && !/chrome\//.test(lower)) browser = 'Safari';
  else if (/firefox\//.test(lower)) browser = 'Firefox';

  return { device_type, device_os, browser };
}

export function trackClick(element: string, metadata?: Record<string, any>): void {
  trackEvent({
    event_type: 'click',
    metadata: { element, ...metadata },
  });
}

export function trackSearch(query: string): void {
  trackEvent({
    event_type: 'search',
    metadata: { query },
  });
}

export function trackError(error: string, metadata?: Record<string, any>): void {
  trackEvent({
    event_type: 'error',
    metadata: { error, ...metadata },
  });
}

/**
 * Page duration tracking — records how long a user stays on each page.
 */
let currentPagePath: string | null = null;
let pageEnteredAt: number = 0;

function sendPageDuration(): void {
  if (currentPagePath && pageEnteredAt) {
    const duration = Math.round((Date.now() - pageEnteredAt) / 1000);
    if (duration > 0 && duration < 3600) { // ignore if > 1 hour (tab left open)
      trackEvent({
        event_type: 'page_duration',
        page_path: currentPagePath,
        metadata: { duration_seconds: duration },
      });
    }
  }
}

export function trackPageView(path?: string): void {
  // Send duration for previous page before tracking new one
  sendPageDuration();

  const newPath = path || window.location.pathname;
  currentPagePath = newPath;
  pageEnteredAt = Date.now();

  trackEvent({
    event_type: 'page_view',
    page_path: newPath,
  });

  // Mirror to Google Tag Manager dataLayer so GA4 (loaded via GTM) sees
  // SPA route changes. GTM's built-in "History Change" trigger catches
  // pushState too, but pushing an explicit `page_view` is more reliable
  // across React Router versions + future GTM tag swaps.
  try {
    const w = window as unknown as {
      dataLayer?: Record<string, unknown>[];
      gtag?: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'page_view',
      page_path: newPath,
      page_location: window.location.href,
      page_title: document.title,
    });
    // If a per-surface gtag.js was loaded for this hostname (see
    // public/index.html), fire a manual page_view too — gtag does not
    // track SPA route changes automatically.
    if (typeof w.gtag === 'function') {
      w.gtag('event', 'page_view', {
        page_path: newPath,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch {
    /* dataLayer push must never break the app */
  }
}

/**
 * Initialize analytics — call once on app load.
 * Tracks initial page view and sets up route change listener.
 */
let initialized = false;
export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  // Track session start
  trackEvent({ event_type: 'session_start' });

  // Track duration + session end when user leaves
  window.addEventListener('beforeunload', () => {
    sendPageDuration();
    trackEvent({ event_type: 'session_end' });
  });

  // Track when tab loses visibility (user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      sendPageDuration();
    } else {
      pageEnteredAt = Date.now();
    }
  });

  // Global click tracking — captures ALL clicks on the page
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target) return;

    // Walk up to find the nearest meaningful element (button, link, input)
    let el: HTMLElement | null = target;
    let tag = '';
    let text = '';
    let href = '';
    let id = '';
    let className = '';

    for (let i = 0; i < 5 && el; i++) {
      tag = el.tagName?.toLowerCase() || '';
      if (['a', 'button', 'input', 'select', 'textarea'].includes(tag) || el.getAttribute('role') === 'button' || el.onclick) {
        text = el.textContent?.trim().substring(0, 100) || '';
        href = (el as HTMLAnchorElement).href || '';
        id = el.id || '';
        className = el.className?.toString().substring(0, 100) || '';
        break;
      }
      el = el.parentElement;
    }

    // Fall back to the original target if no interactive parent found
    if (!tag || !['a', 'button', 'input', 'select', 'textarea'].includes(tag)) {
      tag = target.tagName?.toLowerCase() || '';
      text = target.textContent?.trim().substring(0, 50) || '';
    }

    // Don't track every single div click — only interactive elements
    if (!['a', 'button', 'input', 'select', 'textarea', 'label'].includes(tag) && !el?.getAttribute('role')) return;

    trackEvent({
      event_type: 'click',
      metadata: {
        tag,
        text: text.substring(0, 100),
        href: href || undefined,
        id: id || undefined,
        class: className || undefined,
      },
    });
  }, { passive: true, capture: true });
}
